// src/components/Settings/CategoryCostTypes.js - Modificado para suportar colaboradores
import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase/config";
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { useUserContext } from "../../contexts/UserContext";

const CategoryCostTypes = ({ categoriesData, setError, setSuccess, activeAccountType }) => {
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [costTypes, setCostTypes] = useState({});
  const [initialCostTypes, setInitialCostTypes] = useState({});
  const [isChanged, setIsChanged] = useState(false);
  
  // Access user context to get the active business profile ID and collaborator status
  const { 
    activeBusinessProfileId, 
    getAccountSubpath, 
    isCollaborator, 
    collaboratorWorkspaces 
  } = useUserContext();

  // Carregar classificações de tipo de custo existentes
  useEffect(() => {
    const loadCostTypeSettings = async () => {
      try {
        setLoading(true);
        
        // Verificar se o usuário está autenticado
        if (!auth.currentUser) {
          setError("Usuário não autenticado");
          setLoading(false);
          return;
        }
        
        // Obter o subcaminho para a conta ativa atual usando a função do contexto
        const subcollection = getAccountSubpath();
        
        // Variáveis para armazenar o userId e o docRef
        let userId = auth.currentUser.uid;
        let costTypeDocRef;
        
        // MODIFICADO: Verificar se o usuário é um colaborador para buscar dados do dono
        if (isCollaborator && activeBusinessProfileId) {
          // Encontrar o workspace atual nos colaboratorWorkspaces
          const currentWorkspace = collaboratorWorkspaces.find(w => w.id === activeBusinessProfileId);
          
          if (currentWorkspace && currentWorkspace.ownerUid) {
            // Usar o ID do dono do workspace em vez do ID do colaborador
            userId = currentWorkspace.ownerUid;
            console.log(`Carregando configurações de custo do dono (${userId}) como colaborador`);
          }
        }
        
        // Determinar a coleção e documento a serem usados com base no tipo de conta
        if (activeAccountType === 'personal') {
          // Para conta pessoal, usar a subcoleção "personal"
          costTypeDocRef = doc(db, "personalCostTypes", userId);
        } else {
          // Para conta empresarial, usar subcoleção apropriada baseada no perfil ativo
          if (subcollection === 'business') {
            // Para o perfil principal, verificar primeiro no caminho estruturado
            costTypeDocRef = doc(db, "userCostTypes", userId, "business", "costTypes");
            
            // Verificar se o documento existe
            const docSnapshot = await getDoc(costTypeDocRef);
            
            // Se não existir, tentar o caminho legado
            if (!docSnapshot.exists()) {
              costTypeDocRef = doc(db, "userCostTypes", userId);
            }
          } else {
            // Para outros perfis empresariais, usar o ID do perfil
            costTypeDocRef = doc(db, "userCostTypes", userId, subcollection, "costTypes");
          }
        }
        
        // Buscar configurações de tipo de custo no Firestore
        const costTypeDoc = await getDoc(costTypeDocRef);
        
        if (costTypeDoc.exists()) {
          const data = costTypeDoc.data();
          setCostTypes(data.types || {});
          setInitialCostTypes(data.types || {});
        } else {
          // Documento não existe ainda, inicializar com objeto vazio
          setCostTypes({});
          setInitialCostTypes({});
        }
      } catch (error) {
        console.error("Erro ao carregar tipos de custo:", error);
        setError("Não foi possível carregar as configurações de tipo de custo.");
      } finally {
        setLoading(false);
      }
    };
    
    loadCostTypeSettings();
  }, [setError, activeAccountType, activeBusinessProfileId, getAccountSubpath, isCollaborator, collaboratorWorkspaces]);

  // Verificar se houve mudanças
  useEffect(() => {
    // Comparar o estado atual com o estado inicial
    const checkChanges = () => {
      const keys = new Set([
        ...Object.keys(costTypes),
        ...Object.keys(initialCostTypes)
      ]);
      
      for (const key of keys) {
        if (costTypes[key] !== initialCostTypes[key]) {
          return true;
        }
      }
      
      return false;
    };
    
    setIsChanged(checkChanges());
  }, [costTypes, initialCostTypes]);

  // Alterar o tipo de custo de uma categoria
  const handleCostTypeChange = (categoryPath, costType) => {
    // MODIFICADO: Impedir edição para colaboradores
    if (isCollaborator) {
      setError("Como colaborador, você não tem permissão para modificar os tipos de custo.");
      return;
    }
    
    setCostTypes(prev => ({
      ...prev,
      [categoryPath]: costType
    }));
  };

  // Salvar as alterações no Firestore
  const handleSave = async () => {
    // MODIFICADO: Impedir salvamento para colaboradores
    if (isCollaborator) {
      setError("Como colaborador, você não tem permissão para modificar os tipos de custo.");
      return;
    }
    
    try {
      setSaveLoading(true);
      
      if (!auth.currentUser) {
        setError("Usuário não autenticado");
        setSaveLoading(false);
        return;
      }
      
      // Obter o subcaminho para a conta ativa atual usando a função do contexto
      const subcollection = getAccountSubpath();
      
      // Determinar a coleção e documento a serem usados com base no tipo de conta
      let costTypeRef;
      
      if (activeAccountType === 'personal') {
        // Para conta pessoal, usar a coleção "personalCostTypes"
        costTypeRef = doc(db, "personalCostTypes", auth.currentUser.uid);
      } else {
        // Para conta empresarial, usar subcoleção apropriada
        if (subcollection === 'business') {
          // Para o perfil principal, usar o caminho estruturado
          costTypeRef = doc(db, "userCostTypes", auth.currentUser.uid, "business", "costTypes");
          
          // Também atualizar no caminho legado para compatibilidade
          const legacyRef = doc(db, "userCostTypes", auth.currentUser.uid);
          
          // Verificar se o documento legado já existe
          const legacyDoc = await getDoc(legacyRef);
          
          if (legacyDoc.exists()) {
            // Atualizar também o documento legado
            await updateDoc(legacyRef, {
              types: costTypes,
              updatedAt: new Date()
            });
          } else {
            // Criar o documento legado
            await setDoc(legacyRef, {
              types: costTypes,
              createdAt: new Date(),
              updatedAt: new Date()
            });
          }
        } else {
          // Para outros perfis empresariais, usar o ID do perfil
          costTypeRef = doc(db, "userCostTypes", auth.currentUser.uid, subcollection, "costTypes");
        }
      }
      
      // Verificar se o documento já existe
      const costTypeDoc = await getDoc(costTypeRef);
      
      if (costTypeDoc.exists()) {
        // Atualizar documento existente
        await updateDoc(costTypeRef, {
          types: costTypes,
          updatedAt: new Date()
        });
      } else {
        // Criar novo documento
        await setDoc(costTypeRef, {
          types: costTypes,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      // Atualizar estado inicial para refletir as alterações salvas
      setInitialCostTypes({...costTypes});
      setIsChanged(false);
      
      setSuccess("Classificações de tipo de custo salvas com sucesso!");
      
      // Limpar mensagem após 3 segundos
      setTimeout(() => {
        setSuccess("");
      }, 3000);
    } catch (error) {
      console.error("Erro ao salvar tipos de custo:", error);
      setError("Erro ao salvar as classificações de tipo de custo.");
    } finally {
      setSaveLoading(false);
    }
  };

  // Organizar categorias por grupos
  const organizeCategories = () => {
    if (!categoriesData || !categoriesData.categories) {
      return {
        groupedCategories: {},
        totalCategories: 0
      };
    }

    const categories = categoriesData.categories;
    const categoryOrder = categoriesData.categoryOrder || {};
    
    const selectedKeys = Object.keys(categories).filter(key => categories[key] === true);
    const groupedCategories = {};

    // Processar cada chave
    selectedKeys.forEach(key => {
      const parts = key.split('.');
      
      // Obter o grupo (primeira parte)
      const groupName = parts[0];
      
      // Inicializar o grupo se não existir
      if (!groupedCategories[groupName]) {
        // Usar a ordem se disponível, ou um número alto para categorias sem ordem
        const order = categoryOrder[groupName] || 999;
        groupedCategories[groupName] = {
          normalCategories: [],
          subGroups: {},
          order: order
        };
      }
      
      // Verificar se tem 2 ou 3 partes (com ou sem subgrupo)
      if (parts.length === 2) {
        // Sem subgrupo: grupo.categoria
        groupedCategories[groupName].normalCategories.push({
          name: parts[1],
          path: key
        });
      } else if (parts.length === 3) {
        // Com subgrupo: grupo.subgrupo.categoria
        const subGroupName = parts[1];
        const categoryName = parts[2];
        
        if (!groupedCategories[groupName].subGroups[subGroupName]) {
          groupedCategories[groupName].subGroups[subGroupName] = [];
        }
        
        groupedCategories[groupName].subGroups[subGroupName].push({
          name: categoryName,
          path: key
        });
      }
    });

    return {
      groupedCategories,
      totalCategories: selectedKeys.length
    };
  };

  // Verificar se o grupo principal contém despesas
  const isExpenseGroup = (groupName) => {
    // Verificar se o nome do grupo indica despesa
    return groupName.includes("DESPESAS") || 
           groupName.includes("CUSTOS") || 
           groupName.includes("Despesas") || 
           groupName.includes("Custos") ||
           groupName.startsWith("(-)") ||
           groupName.includes("🧾") ||  // Emoji de despesas fixas
           groupName.includes("🍞");    // Emoji de despesas variáveis
  };

  const { groupedCategories } = organizeCategories();

  if (loading) {
    return <div className="loading-container">
      <div className="loading-spinner"></div>
      <p>Carregando configurações de tipo de custo...</p>
    </div>;
  }

  // MODIFICADO: Adicionar mensagem para colaboradores
  if (isCollaborator) {
    return (
      <div className="cost-type-container">
        <div className="cost-type-description" style={{ backgroundColor: "#f8f0e3", borderLeftColor: "#e67e22" }}>
          <p><strong>Modo Visualização:</strong> Como colaborador, você pode visualizar as classificações de custos fixos e variáveis, mas não pode modificá-las. Apenas o proprietário da conta pode alterar estas configurações.</p>
        </div>
        
        <div className="cost-types-grid">
          {Object.entries(groupedCategories)
            .filter(([groupName]) => isExpenseGroup(groupName))
            .sort(([, dataA], [, dataB]) => {
              return dataA.order - dataB.order;
            })
            .map(([groupName, groupData], index) => (
              <div key={index} className="cost-type-group">
                <div className="cost-type-group-header">{groupName}</div>
                
                {/* Categorias normais (sem subgrupo) */}
                {groupData.normalCategories.length > 0 && (
                  <div className="cost-type-items">
                    {groupData.normalCategories.map((category, catIndex) => (
                      <div key={catIndex} className="cost-type-item">
                        <span className="cost-type-name">{category.name}</span>
                        <div className="cost-type-selector">
                          <div className={`cost-type-option ${costTypes[category.path] === 'fixed' ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                            <span>Fixo</span>
                            {costTypes[category.path] === 'fixed' && 
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            }
                          </div>
                          <div className={`cost-type-option ${costTypes[category.path] === 'variable' ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                            <span>Variável</span>
                            {costTypes[category.path] === 'variable' && 
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            }
                          </div>
                          <div className={`cost-type-option ${!costTypes[category.path] ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                            <span>Não classificado</span>
                            {!costTypes[category.path] && 
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            }
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Categorias com subgrupos */}
                {Object.entries(groupData.subGroups).map(([subGroupName, categories], subIndex) => (
                  <div key={subIndex} className="cost-type-subgroup">
                    <h4 className="subgroup-title">{subGroupName}</h4>
                    <div className="cost-type-items">
                      {categories.map((category, catIndex) => (
                        <div key={catIndex} className="cost-type-item">
                          <span className="cost-type-name">{category.name}</span>
                          <div className="cost-type-selector">
                            <div className={`cost-type-option ${costTypes[category.path] === 'fixed' ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                              <span>Fixo</span>
                              {costTypes[category.path] === 'fixed' && 
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              }
                            </div>
                            <div className={`cost-type-option ${costTypes[category.path] === 'variable' ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                              <span>Variável</span>
                              {costTypes[category.path] === 'variable' && 
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              }
                            </div>
                            <div className={`cost-type-option ${!costTypes[category.path] ? 'selected' : ''}`} style={{ opacity: 0.8 }}>
                              <span>Não classificado</span>
                              {!costTypes[category.path] && 
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', marginLeft: '5px' }}>
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              }
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>
      </div>
    );
  }

  // Versão regular para usuários não-colaboradores
  return (
    <div className="cost-type-container">
      <div className="cost-type-description">
        <p>Classifique suas categorias de despesa como <strong>Custo Fixo</strong> ou <strong>Custo Variável</strong> para melhorar a análise no Dashboard. 
           Custos fixos permanecem constantes independentemente do volume de vendas, enquanto custos variáveis mudam proporcionalmente à atividade do negócio.</p>
      </div>
      
      {isChanged && (
        <div className="unsaved-changes-alert">
          <p>Você tem alterações não salvas</p>
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? "Salvando..." : "Salvar Alterações"}
          </button>
        </div>
      )}
      
      <div className="cost-types-grid">
        {Object.entries(groupedCategories)
          .filter(([groupName]) => isExpenseGroup(groupName))
          .sort(([, dataA], [, dataB]) => {
            return dataA.order - dataB.order;
          })
          .map(([groupName, groupData], index) => (
            <div key={index} className="cost-type-group">
              <div className="cost-type-group-header">{groupName}</div>
              
              {/* Categorias normais (sem subgrupo) */}
              {groupData.normalCategories.length > 0 && (
                <div className="cost-type-items">
                  {groupData.normalCategories.map((category, catIndex) => (
                    <div key={catIndex} className="cost-type-item">
                      <span className="cost-type-name">{category.name}</span>
                      <div className="cost-type-selector">
                        <label className={`cost-type-option ${costTypes[category.path] === 'fixed' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name={`cost-type-${category.path}`}
                            value="fixed"
                            checked={costTypes[category.path] === 'fixed'}
                            onChange={() => handleCostTypeChange(category.path, 'fixed')}
                          />
                          <span>Fixo</span>
                        </label>
                        <label className={`cost-type-option ${costTypes[category.path] === 'variable' ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name={`cost-type-${category.path}`}
                            value="variable"
                            checked={costTypes[category.path] === 'variable'}
                            onChange={() => handleCostTypeChange(category.path, 'variable')}
                          />
                          <span>Variável</span>
                        </label>
                        <label className={`cost-type-option ${!costTypes[category.path] ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name={`cost-type-${category.path}`}
                            value=""
                            checked={!costTypes[category.path]}
                            onChange={() => handleCostTypeChange(category.path, null)}
                          />
                          <span>Não classificado</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Categorias com subgrupos */}
              {Object.entries(groupData.subGroups).map(([subGroupName, categories], subIndex) => (
                <div key={subIndex} className="cost-type-subgroup">
                  <h4 className="subgroup-title">{subGroupName}</h4>
                  <div className="cost-type-items">
                    {categories.map((category, catIndex) => (
                      <div key={catIndex} className="cost-type-item">
                        <span className="cost-type-name">{category.name}</span>
                        <div className="cost-type-selector">
                          <label className={`cost-type-option ${costTypes[category.path] === 'fixed' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name={`cost-type-${category.path}`}
                              value="fixed"
                              checked={costTypes[category.path] === 'fixed'}
                              onChange={() => handleCostTypeChange(category.path, 'fixed')}
                            />
                            <span>Fixo</span>
                          </label>
                          <label className={`cost-type-option ${costTypes[category.path] === 'variable' ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name={`cost-type-${category.path}`}
                              value="variable"
                              checked={costTypes[category.path] === 'variable'}
                              onChange={() => handleCostTypeChange(category.path, 'variable')}
                            />
                            <span>Variável</span>
                          </label>
                          <label className={`cost-type-option ${!costTypes[category.path] ? 'selected' : ''}`}>
                            <input
                              type="radio"
                              name={`cost-type-${category.path}`}
                              value=""
                              checked={!costTypes[category.path]}
                              onChange={() => handleCostTypeChange(category.path, null)}
                            />
                            <span>Não classificado</span>
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
      </div>
      
      {isChanged && (
        <div className="cost-type-actions">
          <button 
            className="save-button"
            onClick={handleSave}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className="button-spinner"></span>
                <span>Salvando...</span>
              </>
            ) : (
              "Salvar Alterações"
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CategoryCostTypes;